/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { getAuthService } from 'src/core/services/authService';
import { TokenSwagger } from 'src/core/services/azure/azureSwaggers';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { getEnvironment } from 'src/core/utils/api/environment';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';

export const getTokensService = () => {
    const { authWebApiUrl } = getEnvironment();

    const apiOptions = luisRequestOptionsBuilder()
        .useContentType('json')
        .useCORS()
        .build();

    /**
     * Gets the ARM neutral token.
     */
    const getNeutralArmToken = async (): Promise<TokenSwagger> => {
        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('token')
            .resource('arm')
            .build();

        try {
            const { data: neutralToken } = await httpService.get<TokenSwagger>(url, apiOptions);

            return neutralToken;
        } catch (error) {
            // Handle the re-authentication silently in case of restarting the web server.
            if ((<Error>error).name === '400') {
                getAuthService().login({});
            }

            throw error;
        }
    };

    /**
     * Gets the tenant specific token for the given tenant id.
     *
     * @param param0.tenantId The id of the tenant to get the token for.
     */
    const getTenantArmToken = async ({ tenantId }: { tenantId: string }): Promise<TokenSwagger> => {
        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('token')
            .resource('arm', tenantId)
            .build();

        const { data: tenantToken } = await httpService.get<TokenSwagger>(url, apiOptions);

        return tenantToken;
    };

    /**
     * Gets the Azure storage token for the given tenant id.
     *
     * @param param0.tenantId The id of the tenant to get the token for.
     */
    const getAzureStorageAccountToken = async ({ tenantId }: { tenantId: string }): Promise<TokenSwagger> => {
        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('token')
            .resource('storage', tenantId)
            .build();

        const { data: storageToken } = await httpService.get<TokenSwagger>(url, apiOptions);

        return storageToken;
    };

    /**
     * Gets the service key object.
     */
    const getServiceKey = async ({
        tenantId,
        armToken,
        principalId,
        resourcePath,
        resourceKind
    }: {
        tenantId: string;
        armToken: string;
        principalId: string;
        resourcePath: string;
        resourceKind: string;
    }): Promise<ServiceKey> => {
        const url = urlBuilder()
            .baseUrl(authWebApiUrl)
            .resource('token')
            .resource('serviceKey', tenantId)
            .build();

        const { data: serviceKey } = await httpService.post<ServiceKey>(
            url,
            { armToken, resourcePath, principalId, resourceKind },
            apiOptions
        );

        return serviceKey;
    };

    return {
        getNeutralArmToken,
        getTenantArmToken,
        getAzureStorageAccountToken,
        getServiceKey
    };
};
